import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Logo } from '../components/Logo.jsx';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

const CONTRATOS = {
  '001-2026': {
    titulo: 'Implementación de plataforma contable LibroContable',
    fecha: '6 de mayo de 2026',
    cliente: { razon: 'SERVICIOS LOGÍSTICOS & CONTABLES S.A.S', nit: '901.587.923-0', repr: 'Diana M. Penagos T.', cc: '29.877.857' },
    contratista: { nombre: 'Cristian Fernando Alvarán', tipo: 'Persona natural', ciudad: 'Tuluá, Valle del Cauca', email: 'cristianse184s@gmail.com' },
    valor: '$ 200.000 COP',
    pago: '50% / 50%',
    plazo: '30 días calendario',
  },
};

function SignaturePad({ onChange, readOnly = false, dataUrl = null, label }) {
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#1B2A4E';

    if (dataUrl) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.offsetWidth, canvas.offsetHeight);
      img.src = dataUrl;
    }
  }, [dataUrl]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches?.[0] ?? e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    if (readOnly) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  };

  const move = (e) => {
    if (!drawing || readOnly) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const end = () => {
    if (!drawing || readOnly) return;
    setDrawing(false);
    const url = canvasRef.current.toDataURL('image/png');
    onChange?.(url, hasDrawn);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    onChange?.(null, false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="eyebrow">{label}</span>
        {!readOnly && hasDrawn && (
          <button onClick={clear} className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted hover:text-ink">Limpiar</button>
        )}
      </div>
      <canvas
        ref={canvasRef}
        className={`w-full h-40 rounded-2xl border-2 border-dashed bg-paper transition ${
          readOnly ? 'border-line cursor-default' : hasDrawn ? 'border-ink cursor-crosshair' : 'border-line cursor-crosshair hover:border-ink/60'
        }`}
        style={{ touchAction: 'none' }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      {readOnly && <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mt-2 text-center">Firma confirmada</div>}
    </div>
  );
}

export default function Firma() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const parteHint = params.get('parte');
  const c = CONTRATOS[id] ?? CONTRATOS['001-2026'];

  const [firmas, setFirmas] = useState([]);
  const [parte, setParte] = useState(parteHint === 'contratista' ? 'contratista' : 'cliente');
  const [nombre, setNombre] = useState('');
  const [documento, setDocumento] = useState('');
  const [firmaUrl, setFirmaUrl] = useState(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const cargar = async () => {
    try {
      const r = await fetch(`${API_URL}/firma/${id}`);
      const data = await r.json();
      setFirmas(data.firmas ?? []);
    } catch (err) {
      setError('No se pudo cargar el estado del contrato.');
    }
  };

  useEffect(() => {
    cargar();
    if (!parteHint && c.cliente.repr) setNombre('');
  }, [id]);

  useEffect(() => {
    if (parteHint === 'cliente') { setNombre(c.cliente.repr); setDocumento(c.cliente.cc); }
    if (parteHint === 'contratista') { setNombre(c.contratista.nombre); }
  }, [parteHint]);

  const firmaCliente = firmas.find((f) => f.parte === 'cliente');
  const firmaContratista = firmas.find((f) => f.parte === 'contratista');
  const ambasFirmadas = !!firmaCliente && !!firmaContratista;
  const yaFirmoMiParte = parte === 'cliente' ? !!firmaCliente : !!firmaContratista;

  const submit = async () => {
    setError('');
    if (!nombre || !firmaUrl || !hasDrawn) {
      setError('Completa nombre y dibuja tu firma antes de confirmar.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/firma`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contrato_id: id,
          parte,
          nombre,
          documento: documento || null,
          firma_dataurl: firmaUrl,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error ?? 'error');
      setSuccess(true);
      await cargar();
    } catch (err) {
      setError(err.message === 'ya_firmado' ? 'Esa parte ya fue firmada.' : 'No se pudo guardar la firma.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      <header className="sticky top-0 z-40 border-b border-line/60 backdrop-blur-md bg-paper/85">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo size={36} withWordmark={false} />
          <div className="leading-none hidden sm:block">
            <div className="font-bold tracking-tight text-[15px]">Firma del contrato</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.18em] text-muted mt-1">N° {id}</div>
          </div>
          <div className="flex items-center gap-2">
            {firmaCliente && <span className="pill-lime">Cliente firmó</span>}
            {firmaContratista && <span className="pill-lime">Contratista firmó</span>}
            {!firmaCliente && !firmaContratista && <span className="pill">Sin firmas aún</span>}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 print:py-0">
        {/* Hero contrato */}
        <div className="card-ink mb-6 grain relative overflow-hidden print:hidden">
          <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(closest-side, #C8E847, transparent)' }} />
          <div className="relative">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-lime mb-3">Contrato N° {id}</p>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight mb-4">{c.titulo}</h1>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <Hero label="Valor" value={c.valor} />
              <Hero label="Forma de pago" value={c.pago} />
              <Hero label="Plazo entrega" value={c.plazo} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="card">
            <p className="eyebrow mb-2">Contratante</p>
            <div className="font-semibold tracking-tight">{c.cliente.razon}</div>
            <div className="font-mono text-xs text-muted mt-2 leading-relaxed">
              NIT {c.cliente.nit}<br />
              Repr. legal: {c.cliente.repr}<br />
              CC {c.cliente.cc}
            </div>
          </div>
          <div className="card">
            <p className="eyebrow mb-2">Contratista</p>
            <div className="font-semibold tracking-tight">{c.contratista.nombre}</div>
            <div className="font-mono text-xs text-muted mt-2 leading-relaxed">
              {c.contratista.tipo}<br />
              {c.contratista.ciudad}<br />
              {c.contratista.email}
            </div>
          </div>
        </div>

        {/* Selector + form */}
        {!ambasFirmadas && !yaFirmoMiParte && !success && (
          <div className="card mb-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="eyebrow mb-1">Tu firma</p>
                <h2 className="text-xl font-semibold tracking-tight">¿Quién firma ahora?</h2>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6">
              {[
                { v: 'cliente', l: 'Soy ' + c.cliente.repr.split(' ').slice(0, 2).join(' '), sub: 'CONTRATANTE · SLC S.A.S', disabled: !!firmaCliente },
                { v: 'contratista', l: 'Soy ' + c.contratista.nombre.split(' ').slice(0, 2).join(' '), sub: 'CONTRATISTA', disabled: !!firmaContratista },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => { setParte(opt.v); setNombre(opt.v === 'cliente' ? c.cliente.repr : c.contratista.nombre); setDocumento(opt.v === 'cliente' ? c.cliente.cc : ''); }}
                  disabled={opt.disabled}
                  className={`p-4 rounded-2xl text-left transition-all duration-200 ${
                    parte === opt.v && !opt.disabled
                      ? 'bg-ink text-paper shadow-[0_8px_20px_-8px_rgba(27,42,78,0.5)]'
                      : opt.disabled ? 'bg-bone/40 text-muted cursor-not-allowed' : 'bg-bone hover:bg-line'
                  }`}
                >
                  <div className="font-semibold tracking-tight text-sm">{opt.l}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.18em] mt-1 opacity-70">
                    {opt.disabled ? 'Ya firmó ✓' : opt.sub}
                  </div>
                </button>
              ))}
            </div>

            <div className="space-y-4">
              <div>
                <label className="label">Nombre completo</label>
                <input className="input" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
              </div>
              <div>
                <label className="label">{parte === 'cliente' ? 'Cédula' : 'Documento (opcional)'}</label>
                <input className="input" value={documento} onChange={(e) => setDocumento(e.target.value)} placeholder={parte === 'cliente' ? '29.877.857' : 'CC o NIT'} />
              </div>
              <SignaturePad
                label="Dibuja tu firma con el dedo (móvil) o el mouse"
                onChange={(url, drawn) => { setFirmaUrl(url); setHasDrawn(drawn); }}
              />
              {error && (
                <div className="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 font-mono">{error}</div>
              )}
              <button onClick={submit} className="btn-primary w-full" disabled={loading || !hasDrawn}>
                {loading ? 'Guardando…' : 'Confirmar mi firma →'}
              </button>
            </div>
          </div>
        )}

        {/* Estado firmas confirmadas */}
        {(yaFirmoMiParte || ambasFirmadas) && (
          <div className="card mb-6">
            <p className="eyebrow mb-4">Firmas confirmadas</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FirmaBlock firma={firmaCliente} parte="cliente" cliente={c.cliente} />
              <FirmaBlock firma={firmaContratista} parte="contratista" contratista={c.contratista} />
            </div>

            {!ambasFirmadas && (
              <div className="card-lime mt-6 p-4 text-center">
                <div className="font-semibold tracking-tight">Esperando a que firme la otra parte.</div>
                <div className="font-mono text-[11px] text-muted mt-2">Comparte este link: <span className="text-ink">{typeof window !== 'undefined' ? window.location.href : ''}</span></div>
              </div>
            )}

            {ambasFirmadas && (
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <button onClick={() => window.print()} className="btn-primary">
                  Imprimir / Guardar PDF firmado
                </button>
              </div>
            )}
          </div>
        )}

        {success && yaFirmoMiParte && (
          <div className="card-lime text-center p-8 mb-6">
            <div className="text-5xl mb-4">✓</div>
            <div className="text-2xl font-semibold tracking-tight mb-2">Tu firma quedó registrada.</div>
            <div className="text-muted text-sm">Recarga la página o comparte el link con la otra parte para que firme también.</div>
          </div>
        )}
      </main>
    </div>
  );
}

function Hero({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-lime mb-1">{label}</div>
      <div className="text-xl font-semibold tracking-tight">{value}</div>
    </div>
  );
}

function FirmaBlock({ firma, parte, cliente, contratista }) {
  const datos = parte === 'cliente' ? cliente : contratista;
  if (!firma) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-line p-4 h-40 flex items-center justify-center text-muted text-sm font-mono">
        Pendiente firma · {parte}
      </div>
    );
  }
  return (
    <div>
      <div className="rounded-2xl bg-paper border border-line p-4 mb-3">
        <img src={firma.firma_dataurl} alt={`Firma ${parte}`} className="w-full h-32 object-contain" />
      </div>
      <div className="font-semibold tracking-tight">{firma.nombre}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mt-1">
        {parte === 'cliente' ? 'CONTRATANTE' : 'CONTRATISTA'} · {firma.documento ?? ''} · {new Date(firma.created_at).toLocaleDateString('es-CO')}
      </div>
    </div>
  );
}
